import { useQuery } from '@tanstack/react-query';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import TiptapContent from '../../../../components/TiptapContent';
import { fetchLatestEventByType } from '../../../../services/patient/timeline';
import { formatCreatedDate } from '../../../../utils/dates';

interface ReviewNotesPaneProps {
    patientId: string | null;
    /** Timeline event type carrying the ODO clinical notes (Tiptap doc). */
    triggerType: 'refinement_trigger' | 'retention_trigger';
    /** Plain-text Monday fallback when no rich notes event exists. */
    fallbackNotes: string | null;
    /** Scan submission checklist link. */
    checklistUrl: string;
    checklistLabel: string;
    /** Sizing for the host slot: the left column or the Case tab. */
    className: string;
}

/** ODO's recommendation for the review pages (trigger-event Tiptap doc, Monday text fallback) plus the scan checklist link. */
export function ReviewNotesPane({
    patientId,
    triggerType,
    fallbackNotes,
    checklistUrl,
    checklistLabel,
    className
}: ReviewNotesPaneProps) {
    const { data: triggerEvent } = useQuery({
        queryKey: ['timeline', patientId, triggerType],
        queryFn: () => fetchLatestEventByType(patientId!, triggerType),
        enabled: !!patientId,
        staleTime: 60_000
    });

    const notesDoc = triggerEvent?.payload?.notesDoc;

    return (
        <div
            className={`${className} bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter`}
        >
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
                <span className="font-bold text-sm text-gray-800">ODO Recommendation</span>
                {triggerEvent && (
                    <span className="text-xs text-gray-500">
                        {formatCreatedDate(triggerEvent.createdAt)}
                    </span>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 scrollbar-hide">
                <div className="text-sm text-gray-700 leading-relaxed break-words">
                    {notesDoc && patientId ? (
                        <TiptapContent doc={notesDoc} patientId={patientId} />
                    ) : (
                        fallbackNotes || 'No notes available.'
                    )}
                </div>
            </div>

            {/* Checklist link, pinned at the bottom of the pane. */}
            <div className="flex-shrink-0 border-t border-gray-100 px-5 py-2.5">
                <a
                    href={checklistUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#3773A8] hover:underline"
                >
                    <DocumentTextIcon className="w-4 h-4 flex-shrink-0" />
                    {checklistLabel}
                </a>
            </div>
        </div>
    );
}
